import React, { useState, useMemo, useCallback } from 'react';
import { motion } from 'framer-motion';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip as RechartsTooltip } from 'recharts';
import toast from 'react-hot-toast';
import { fetchWalletPositions } from '../zerionApi';
import { useWallet } from '../contexts/WalletContext';
import { useTheme } from '../theme';
import { useIsMobile } from '../useMediaQuery';
import ProGate from './ProGate';
import { HiOutlineSearch, HiOutlineRefresh } from 'react-icons/hi';

const PIE_COLORS = ['#0D7A4E', '#5470D4', '#E8471A', '#E0006E', '#1A7A56', '#8A6A00', '#6B35C8', '#1A8ED4', '#D40010', '#0040D4'];

function fmtUsd(v) {
  if (v == null || isNaN(v)) return '$0.00';
  if (Math.abs(v) >= 1e6) return `$${(v / 1e6).toFixed(2)}M`;
  if (Math.abs(v) >= 1e3) return `$${(v / 1e3).toFixed(1)}K`;
  return `$${v.toFixed(2)}`;
}

export default function ProWalletAnalyzerView({ pools }) {
  const { colors, dark } = useTheme();
  const isMobile = useIsMobile();
  const { account, isConnected, connect } = useWallet();

  const [address, setAddress] = useState('');
  const [positions, setPositions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [analyzed, setAnalyzed] = useState(false);

  const targetAddress = address || account;

  const analyze = useCallback(async () => {
    if (!targetAddress || !/^0x[0-9a-fA-F]{40}$/.test(targetAddress)) {
      toast.error('Invalid wallet address');
      return;
    }

    setLoading(true);
    try {
      const pos = await fetchWalletPositions(targetAddress);
      setPositions(pos || []);
      setAnalyzed(true);
      toast.success(`Found ${(pos || []).length} positions`);
    } catch (err) {
      toast.error('Analysis failed. Check that the API key is configured.');
      console.error(err);
    }
    setLoading(false);
  }, [targetAddress]);

  const totalValue = useMemo(() =>
    positions.reduce((s, p) => s + (isNaN(p.totalValueUsd) ? 0 : (p.totalValueUsd || 0)), 0),
    [positions]
  );

  const byProtocol = useMemo(() => {
    const map = {};
    positions.forEach(p => {
      const key = p.protocolName || 'Unknown';
      if (!map[key]) map[key] = { name: key, value: 0, count: 0 };
      const val = isNaN(p.totalValueUsd) ? 0 : (p.totalValueUsd || 0);
      map[key].value += val;
      map[key].count++;
    });
    return Object.values(map).sort((a, b) => b.value - a.value);
  }, [positions]);

  const byChain = useMemo(() => {
    const map = {};
    positions.forEach(p => {
      const key = p.chain || 'Unknown';
      if (!map[key]) map[key] = { name: key, value: 0 };
      const val = isNaN(p.totalValueUsd) ? 0 : (p.totalValueUsd || 0);
      map[key].value += val;
    });
    return Object.values(map).sort((a, b) => b.value - a.value);
  }, [positions]);

  // Find optimization suggestions
  const suggestions = useMemo(() => {
    if (!positions.length || !pools?.length) return [];
    const suggs = [];

    positions.forEach(pos => {
      // Find better alternatives for each position
      const posTokens = pos.tokens?.map(t => t.symbol?.toUpperCase()) || [];
      if (!posTokens.length) return;

      // Find pools with matching tokens that have higher APR
      const alternatives = pools.filter(pool => {
        const poolTokens = (pool.tokens || []).map(t => t.toUpperCase());
        return posTokens.some(t => poolTokens.includes(t)) && pool.stablecoin;
      }).sort((a, b) => b.apr - a.apr).slice(0, 1);

      if (alternatives.length > 0 && alternatives[0].apr > 0) {
        const currentApr = pos.apr || 0;
        const altApr = alternatives[0].apr;
        const improvement = currentApr > 0
          ? ((altApr - currentApr) / currentApr) * 100
          : altApr;
        suggs.push({
          current: pos,
          alternative: alternatives[0],
          improvement,
        });
      }
    });

    return suggs.sort((a, b) => b.improvement - a.improvement).slice(0, 5);
  }, [positions, pools]);

  const content = (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      style={{ maxWidth: 1000, margin: '0 auto', padding: isMobile ? '16px 12px' : '24px' }}
    >
      {/* Header */}
      <div style={{ marginBottom: 20 }}>
        <div style={{
          fontFamily: "'DM Serif Display', serif",
          fontSize: isMobile ? 24 : 28,
          color: colors.textPrimary,
          marginBottom: 4,
        }}>
          Multi-Protocol Analyzer
        </div>
        <div style={{
          fontFamily: "'IBM Plex Mono', monospace",
          fontSize: 12,
          color: colors.textSecondary,
        }}>
          Analyze DeFi positions across all protocols and chains via Zerion
        </div>
      </div>

      {/* Search */}
      <div style={{
        display: 'flex',
        gap: 8,
        marginBottom: 24,
      }}>
        <div style={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          background: colors.surface,
          border: `1px solid ${colors.border}`,
          borderRadius: 10,
          padding: '0 14px',
        }}>
          <HiOutlineSearch size={16} style={{ color: colors.labelMuted, flexShrink: 0 }} />
          <input
            type="text"
            value={address}
            onChange={e => setAddress(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && analyze()}
            placeholder={isConnected ? `Connected: ${account?.slice(0, 8)}...` : 'Enter wallet address (0x...)'}
            style={{
              fontFamily: "'IBM Plex Mono', monospace",
              fontSize: 13,
              color: colors.textPrimary,
              background: 'transparent',
              border: 'none',
              outline: 'none',
              width: '100%',
              padding: '12px 0',
            }}
          />
        </div>
        {!isConnected && (
          <button
            onClick={connect}
            style={{
              fontFamily: "'IBM Plex Mono', monospace",
              fontSize: 11,
              fontWeight: 600,
              color: colors.green,
              background: colors.greenBg,
              border: `1px solid ${colors.greenBorder}`,
              borderRadius: 10,
              padding: '0 16px',
              cursor: 'pointer',
              whiteSpace: 'nowrap',
            }}
          >
            Connect
          </button>
        )}
        <button
          onClick={analyze}
          disabled={loading}
          style={{
            fontFamily: "'IBM Plex Mono', monospace",
            fontSize: 12,
            fontWeight: 600,
            color: '#FFFFFF',
            background: colors.green,
            border: 'none',
            borderRadius: 10,
            padding: '0 20px',
            cursor: loading ? 'default' : 'pointer',
            opacity: loading ? 0.7 : 1,
            display: 'flex',
            alignItems: 'center',
            gap: 6,
          }}
        >
          <HiOutlineRefresh size={14} style={{ animation: loading ? 'spin 1s linear infinite' : 'none' }} />
          Analyze
        </button>
      </div>

      {loading && (
        <div style={{
          textAlign: 'center',
          padding: 40,
          fontFamily: "'IBM Plex Mono', monospace",
          fontSize: 12,
          color: colors.labelMuted,
        }}>
          Scanning all protocols across all chains...
        </div>
      )}

      {analyzed && !loading && (
        <>
          {/* Portfolio overview */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr 1fr' : '1fr 1fr 1fr 1fr',
            gap: 12,
            marginBottom: 20,
          }}>
            <div style={{
              background: colors.surface,
              borderRadius: 10,
              border: `1px solid ${colors.border}`,
              padding: 16,
            }}>
              <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 9, color: colors.labelLight, letterSpacing: 1, marginBottom: 6 }}>
                TOTAL VALUE
              </div>
              <div style={{ fontFamily: "'DM Serif Display', serif", fontSize: 24, color: colors.green }}>
                {fmtUsd(totalValue)}
              </div>
            </div>
            <div style={{
              background: colors.surface,
              borderRadius: 10,
              border: `1px solid ${colors.border}`,
              padding: 16,
            }}>
              <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 9, color: colors.labelLight, letterSpacing: 1, marginBottom: 6 }}>
                POSITIONS
              </div>
              <div style={{ fontFamily: "'DM Serif Display', serif", fontSize: 24, color: colors.textPrimary }}>
                {positions.length}
              </div>
            </div>
            <div style={{
              background: colors.surface,
              borderRadius: 10,
              border: `1px solid ${colors.border}`,
              padding: 16,
            }}>
              <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 9, color: colors.labelLight, letterSpacing: 1, marginBottom: 6 }}>
                PROTOCOLS
              </div>
              <div style={{ fontFamily: "'DM Serif Display', serif", fontSize: 24, color: colors.textPrimary }}>
                {byProtocol.length}
              </div>
            </div>
            <div style={{
              background: colors.surface,
              borderRadius: 10,
              border: `1px solid ${colors.border}`,
              padding: 16,
            }}>
              <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 9, color: colors.labelLight, letterSpacing: 1, marginBottom: 6 }}>
                CHAINS
              </div>
              <div style={{ fontFamily: "'DM Serif Display', serif", fontSize: 24, color: colors.textPrimary }}>
                {byChain.length}
              </div>
            </div>
          </div>

          {/* Charts row */}
          {byProtocol.length > 0 && (
            <div style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
              gap: 16,
              marginBottom: 20,
            }}>
              {/* By Protocol */}
              <div style={{
                background: colors.surface,
                borderRadius: 12,
                border: `1px solid ${colors.border}`,
                padding: 20,
              }}>
                <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 10, color: colors.labelLight, letterSpacing: 1, marginBottom: 12 }}>
                  BY PROTOCOL
                </div>
                <ResponsiveContainer width="100%" height={180}>
                  <PieChart>
                    <Pie data={byProtocol} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={50} outerRadius={80}>
                      {byProtocol.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                    </Pie>
                    <RechartsTooltip
                      formatter={(val) => fmtUsd(val)}
                      contentStyle={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 11, borderRadius: 8, border: `1px solid ${colors.border}` }}
                    />
                  </PieChart>
                </ResponsiveContainer>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 8 }}>
                  {byProtocol.slice(0, 5).map((p, i) => (
                    <div key={p.name} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <div style={{ width: 8, height: 8, borderRadius: 2, background: PIE_COLORS[i % PIE_COLORS.length] }} />
                        <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 11, color: colors.textPrimary }}>{p.name}</span>
                      </div>
                      <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 11, color: colors.textSecondary }}>
                        {fmtUsd(p.value)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* By Chain */}
              <div style={{
                background: colors.surface,
                borderRadius: 12,
                border: `1px solid ${colors.border}`,
                padding: 20,
              }}>
                <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 10, color: colors.labelLight, letterSpacing: 1, marginBottom: 12 }}>
                  BY CHAIN
                </div>
                <ResponsiveContainer width="100%" height={180}>
                  <PieChart>
                    <Pie data={byChain} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={50} outerRadius={80}>
                      {byChain.map((_, i) => <Cell key={i} fill={PIE_COLORS[(i + 3) % PIE_COLORS.length]} />)}
                    </Pie>
                    <RechartsTooltip
                      formatter={(val) => fmtUsd(val)}
                      contentStyle={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 11, borderRadius: 8, border: `1px solid ${colors.border}` }}
                    />
                  </PieChart>
                </ResponsiveContainer>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 8 }}>
                  {byChain.slice(0, 5).map((c, i) => (
                    <div key={c.name} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <div style={{ width: 8, height: 8, borderRadius: 2, background: PIE_COLORS[(i + 3) % PIE_COLORS.length] }} />
                        <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 11, color: colors.textPrimary }}>{c.name}</span>
                      </div>
                      <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 11, color: colors.textSecondary }}>
                        {fmtUsd(c.value)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Positions table */}
          <div style={{
            background: colors.surface,
            borderRadius: 12,
            border: `1px solid ${colors.border}`,
            overflow: 'hidden',
            marginBottom: 20,
          }}>
            <div style={{
              padding: '12px 16px',
              background: dark ? 'rgba(255,255,255,0.03)' : '#FAFAFA',
              borderBottom: `1px solid ${colors.border}`,
              fontFamily: "'IBM Plex Mono', monospace",
              fontSize: 10,
              fontWeight: 600,
              color: colors.labelLight,
              letterSpacing: 1,
            }}>
              ALL POSITIONS ({positions.length})
            </div>
            {positions.length === 0 ? (
              <div style={{ padding: 30, textAlign: 'center', fontFamily: "'IBM Plex Mono', monospace", fontSize: 12, color: colors.labelMuted }}>
                No DeFi positions found for this wallet
              </div>
            ) : (
              positions.map((pos, i) => (
                <div key={i} style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '14px 16px',
                  borderBottom: `1px solid ${dark ? 'rgba(255,255,255,0.04)' : '#F4F5F6'}`,
                }}>
                  <div>
                    <div style={{
                      fontFamily: "'IBM Plex Mono', monospace",
                      fontSize: 13,
                      color: colors.textPrimary,
                      fontWeight: 500,
                    }}>
                      {pos.poolName || pos.protocolName || 'Unknown'}
                    </div>
                    <div style={{
                      fontFamily: "'IBM Plex Mono', monospace",
                      fontSize: 10,
                      color: colors.labelMuted,
                      marginTop: 2,
                      display: 'flex',
                      gap: 8,
                    }}>
                      <span>{pos.protocolName}</span>
                      <span>{pos.chain}</span>
                      <span style={{ textTransform: 'uppercase' }}>{pos.type}</span>
                    </div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{
                      fontFamily: "'DM Serif Display', serif",
                      fontSize: 16,
                      color: colors.textPrimary,
                    }}>
                      {fmtUsd(pos.totalValueUsd)}
                    </div>
                    {pos.changes?.percent_1d != null && (
                      <div style={{
                        fontFamily: "'IBM Plex Mono', monospace",
                        fontSize: 10,
                        color: pos.changes.percent_1d >= 0 ? colors.green : '#B8001A',
                      }}>
                        {pos.changes.percent_1d >= 0 ? '+' : ''}{pos.changes.percent_1d.toFixed(2)}%
                      </div>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Optimization suggestions */}
          {suggestions.length > 0 && (
            <div style={{
              background: colors.surface,
              borderRadius: 12,
              border: `1px solid ${dark ? 'rgba(138,106,0,0.3)' : '#E0CC70'}`,
              overflow: 'hidden',
            }}>
              <div style={{
                padding: '12px 16px',
                background: dark ? 'rgba(138,106,0,0.08)' : '#FFF8E0',
                borderBottom: `1px solid ${dark ? 'rgba(138,106,0,0.2)' : '#E0CC70'}`,
                fontFamily: "'IBM Plex Mono', monospace",
                fontSize: 10,
                fontWeight: 600,
                color: '#8A6A00',
                letterSpacing: 1,
              }}>
                &#x1F4A1; OPTIMIZATION SUGGESTIONS
              </div>
              {suggestions.map((s, i) => (
                <div key={i} style={{
                  padding: '12px 16px',
                  borderBottom: `1px solid ${dark ? 'rgba(255,255,255,0.04)' : '#F4F5F6'}`,
                  fontFamily: "'IBM Plex Mono', monospace",
                  fontSize: 12,
                  color: colors.textPrimary,
                }}>
                  <strong>{s.current.poolName || s.current.protocolName}</strong>
                  <span style={{ color: colors.textSecondary }}> on {s.current.protocolName} &rarr; </span>
                  <strong style={{ color: colors.green }}>{s.alternative.name}</strong>
                  <span style={{ color: colors.textSecondary }}> on {s.alternative.protocol}</span>
                  <span style={{
                    fontFamily: "'DM Serif Display', serif",
                    fontSize: 14,
                    color: colors.green,
                    marginLeft: 8,
                  }}>
                    {s.alternative.apr.toFixed(2)}% APR
                  </span>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </motion.div>
  );

  return (
    <>
      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
      <ProGate feature="multi-protocol wallet analysis">
        {content}
      </ProGate>
    </>
  );
}
